import { useEffect, useState } from "react";
import { api, apiError, money } from "@/lib/api";
import { Loader, MetricCard, Money } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { TrendingUp, Repeat, XCircle, IndianRupee, Percent, Pencil } from "lucide-react";

export default function Subscriptions() {
  const [d, setD] = useState(null);
  const [plans, setPlans] = useState([]);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/subscriptions").then((r) => setD(r.data)).catch(() => setD({}));
    api.get("/plans").then((r) => setPlans(r.data));
  };
  useEffect(() => { load(); }, []);
  if (!d) return <Loader />;

  function openEdit(p) {
    setEdit(p);
    setForm({ ...p, features: (p.features || []).join("\n"),
      price: p.price ?? "", transaction_fee_pct: p.transaction_fee_pct ?? "" });
  }

  async function save() {
    setBusy(true);
    try {
      const payload = {
        segment: edit.segment, name: form.name, target: form.target || "", cta: form.cta || "Choose Plan",
        recommended: !!form.recommended, contact: !!form.contact, order: edit.order,
        price: form.price === "" ? null : Number(form.price),
        transaction_fee_pct: form.transaction_fee_pct === "" ? null : Number(form.transaction_fee_pct),
        features: form.features.split("\n").map((s) => s.trim()).filter(Boolean),
        limits: edit.limits || {},
      };
      await api.put(`/admin/plans/${edit.id}`, payload);
      toast.success("Plan updated"); setEdit(null); load();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  }

  return (
    <div className="space-y-6">
      <div><h1 className="font-heading text-2xl font-semibold text-forest">Subscriptions & Revenue</h1>
        <p className="text-sm text-ink/60">Recurring revenue, active plans and pricing controls.</p></div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <MetricCard testid="sub-mrr" label="MRR" value={<Money value={d.mrr} />} icon={IndianRupee} accent />
        <MetricCard testid="sub-arr" label="ARR" value={<Money value={d.arr} />} icon={TrendingUp} accent />
        <MetricCard testid="sub-active" label="Active Subscriptions" value={<span className="num">{d.active_subscriptions}</span>} icon={Repeat} />
        <MetricCard testid="sub-cancelled" label="Cancelled" value={<span className="num">{d.cancelled_subscriptions}</span>} icon={XCircle} />
      </div>

      <div>
        <p className="overline mb-2 text-olive">Revenue by plan</p>
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-psage/40 text-left"><tr className="overline text-forest"><th className="px-4 py-3">Plan</th><th className="px-4 py-3">Subscribers</th><th className="px-4 py-3">MRR</th></tr></thead>
            <tbody>
              {(d.revenue_by_plan || []).length === 0 ? <tr><td colSpan={3} className="px-4 py-6 text-center text-ink/50">No paid subscribers yet</td></tr> :
                d.revenue_by_plan.map((r) => (
                  <tr key={r.plan_id} data-testid={`rev-${r.plan_id}`} className="border-t border-border">
                    <td className="px-4 py-3 text-forest">{r.plan_name}</td>
                    <td className="px-4 py-3 num">{r.subscribers}</td>
                    <td className="px-4 py-3 num text-forest">{money(r.mrr)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <p className="overline mb-2 text-olive">Plan & pricing controls</p>
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-psage/40 text-left"><tr className="overline text-forest">
              <th className="px-4 py-3">Plan</th><th className="px-4 py-3">Segment</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Commission</th><th className="px-4 py-3"></th></tr></thead>
            <tbody>
              {plans.sort((a, b) => a.segment.localeCompare(b.segment) || a.order - b.order).map((p) => (
                <tr key={p.id} data-testid={`plan-row-${p.id}`} className="border-t border-border">
                  <td className="px-4 py-3 text-forest">{p.name} {p.recommended && <span className="ml-1 rounded bg-lime px-1.5 text-[10px] font-bold text-ink">REC</span>}</td>
                  <td className="px-4 py-3 capitalize text-ink/70">{p.segment}</td>
                  <td className="px-4 py-3 num">{p.price === null ? "Custom" : money(p.price)}</td>
                  <td className="px-4 py-3 num">{p.transaction_fee_pct != null ? `${p.transaction_fee_pct}%` : "—"}</td>
                  <td className="px-4 py-3"><Button data-testid={`edit-plan-${p.id}`} size="sm" variant="outline" className="border-forest text-forest hover:bg-psage" onClick={() => openEdit(p)}><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent data-testid="plan-edit-modal">
          <DialogHeader><DialogTitle>Edit plan · {edit?.name}</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            <div><Label className="text-xs">Name</Label><Input data-testid="pe-name" value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Price (₹/mo, blank = custom)</Label><Input data-testid="pe-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1" /></div>
              <div><Label className="text-xs">Commission %</Label><Input data-testid="pe-fee" type="number" value={form.transaction_fee_pct} onChange={(e) => setForm({ ...form, transaction_fee_pct: e.target.value })} className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Target audience</Label><Input data-testid="pe-target" value={form.target || ""} onChange={(e) => setForm({ ...form, target: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">CTA label</Label><Input data-testid="pe-cta" value={form.cta || ""} onChange={(e) => setForm({ ...form, cta: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Features (one per line)</Label>
              <textarea data-testid="pe-features" value={form.features || ""} onChange={(e) => setForm({ ...form, features: e.target.value })} rows={6} className="mt-1 w-full rounded-md border border-border bg-white p-2.5 text-sm" /></div>
            <label className="flex items-center gap-2 text-sm"><input data-testid="pe-recommended" type="checkbox" checked={!!form.recommended} onChange={(e) => setForm({ ...form, recommended: e.target.checked })} /> Mark as recommended</label>
          </div>
          <DialogFooter><Button data-testid="pe-save" className="bg-forest text-white hover:bg-forest/90" disabled={busy} onClick={save}>Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
